import * as Blockly from 'blockly/core';
import { blockIcon } from '@/(routers)/boards/editor/block/blockFactory';
import { defineMutableBlock } from '@/(routers)/boards/editor/block/blockRegistry';
import { CATEGORIES } from '@/(routers)/boards/editor/block/definitions/categories';
import { FieldOperatorDropdown } from '@/(routers)/boards/editor/block/definitions/fields/fieldOperatorDropdown';
import { FieldSegmentedToggle } from '@/(routers)/boards/editor/block/definitions/fields/fieldSegmentedToggle';
import { FieldStyledLabel } from '@/(routers)/boards/editor/block/definitions/fields/fieldStyledLabel';

const COLOR = CATEGORIES.display.color;
const ICON_ADD = { src: '/images/block/btn_add.svg', size: 20 };
const ICON_REMOVE = { src: '/images/block/btn_remove.svg', size: 20 };
const ICON_OLED = '/images/block/oled.svg';

const SIZE_MAX_ROWS: Record<string, number> = {
    '1': 3,
    '2': 2,
    '3': 1,
};

const SIZE_MAX_COLS: Record<string, number> = {
    '1': 16,
    '2': 8,
    '3': 5,
};

const POSITION_OPTIONS = {
    orange: {
        rectColour: CATEGORIES.display.color,
        options: [{ value: 'UPPER_LEFT', label: '左上' }],
    },
    blue: {
        rectColour: CATEGORIES.display.color,
        options: [
            { value: 'UPPER_LEFT', label: '左上' },
            { value: 'LOWER_LEFT', label: '左下' },
            { value: 'UPPER_RIGHT', label: '右上' },
            { value: 'LOWER_RIGHT', label: '右下' },
        ],
    },
} as const;

function getIds(block: Blockly.Block): string[] {
    return (block as any)._ids as string[];
}

function getTypes(block: Blockly.Block): Record<string, 'number' | 'string'> {
    return (block as any)._types as Record<string, 'number' | 'string'>;
}

function getCurrentSize(block: Blockly.Block): string {
    return block.getFieldValue('TEXT_SIZE') ?? '1';
}

function appendHeader(block: Blockly.Block): void {
    block
        .appendDummyInput('HEADER')
        .appendField(blockIcon({ src: ICON_OLED, size: 65 }))
        .appendField(new FieldStyledLabel('OLED', { fontSize: 15, fontWeight: 'bold' }))
        .appendField(' ')
        .appendField(
            new FieldOperatorDropdown(
                POSITION_OPTIONS.orange.options as unknown as { value: string; label: string }[],
                undefined,
                undefined,
                POSITION_OPTIONS.orange.rectColour,
            ),
            'POSITION',
        )
        .appendField('に、文字サイズは')
        .appendField(
            new FieldOperatorDropdown(
                [
                    { value: '1', label: '小', tooltip: '3行表示（英数記号・かなカナ）可' },
                    { value: '2', label: '中', tooltip: '2行表示（英数記号・かなカナ）可' },
                    { value: '3', label: '大', tooltip: '1行表示（英数記号・かなカナ）可' },
                ],
                function (this: FieldOperatorDropdown, value: string) {
                    const b = this.getSourceBlock();
                    if (!b) return value;
                    trimStringOledValues(b, value);
                    requestAnimationFrame(() => {
                        trimRowsToMax(b, value);
                        refreshFooter(b);
                        (b as Blockly.BlockSvg).render();
                    });
                    return value;
                },
            ),
            'TEXT_SIZE',
        )
        .appendField('、背景色は')
        .appendField(
            new FieldOperatorDropdown([
                { value: 'BLACK', label: '黒' },
                { value: 'WHITE', label: '白' },
            ]),
            'BG_COLOR',
        )
        .appendField('で')
        .appendField(
            new Blockly.FieldImage(ICON_ADD.src, ICON_ADD.size, ICON_ADD.size, '+', function () {
                addRow(block);
            }),
            'ADD',
        )
        .appendField(
            new Blockly.FieldImage(ICON_REMOVE.src, ICON_REMOVE.size, ICON_REMOVE.size, '-', function () {
                removeLastRow(block);
            }),
            'REMOVE',
        );
}

function refreshFooter(block: Blockly.Block): void {
    if (!block.workspace) return;
    if (block.getInput('FOOTER')) block.removeInput('FOOTER');
    try {
        block.appendValueInput('FOOTER').setCheck('__none__').appendField(new Blockly.FieldLabel('を表示する'));
    } catch {
        requestAnimationFrame(() => refreshFooter(block));
    }
}

function attachShadow(block: Blockly.Block, inputName: string, type: 'number' | 'string'): void {
    const input = block.getInput(inputName);
    if (!input?.connection || input.connection.targetBlock()) return;
    const shadowType = type === 'string' ? 'string_oled' : type;
    const shadow = (block.workspace as Blockly.WorkspaceSvg).newBlock(shadowType);
    shadow.setShadow(true);
    shadow.outputConnection?.connect(input.connection);
    requestAnimationFrame(() => {
        (shadow as Blockly.BlockSvg).initSvg();
        (shadow as Blockly.BlockSvg).render();
    });
}

function switchType(block: Blockly.Block, id: string, type: 'number' | 'string'): void {
    const types = getTypes(block);
    types[id] = type;

    const input = block.getInput(`VAL_${id}`);
    if (!input?.connection) return;

    Blockly.Events.disable();
    try {
        const existing = input.connection.targetBlock() as Blockly.BlockSvg | null;
        if (existing) {
            input.connection.disconnect();
            existing.getSvgRoot()?.remove();
            existing.dispose(false);
        }
        input.connection.setShadowDom(null);
        (input.connection as any).shadowState_ = null;
        input.connection.setCheck(type === 'number' ? 'Number' : 'String');
        const ws = block.workspace as Blockly.WorkspaceSvg;
        const shadowType = type === 'string' ? 'string_oled' : type;
        const shadow = ws.newBlock(shadowType) as Blockly.BlockSvg;
        shadow.setShadow(true);
        shadow.initSvg();
        shadow.outputConnection?.connect(input.connection);
        shadow.render();
        (block as Blockly.BlockSvg).render();
        ws.render();
    } finally {
        Blockly.Events.enable();
    }
}

function appendRow(block: Blockly.Block, id: string): void {
    const type = getTypes(block)[id] ?? 'number';
    block
        .appendValueInput(`VAL_${id}`)
        .setCheck(type === 'number' ? 'Number' : 'String')
        .appendField(
            new FieldSegmentedToggle(type, {
                options: [
                    { value: 'number', label: '数値' },
                    { value: 'string', label: '文字列' },
                ],
                activeColor: '#FFFFFF',
                onChange: (next: string) => {
                    switchType(block, id, next as 'number' | 'string');
                },
            }),
            `TYPE_${id}`,
        );
    attachShadow(block, `VAL_${id}`, type);
}

function trimStringOledValues(block: Blockly.Block, size: string): void {
    const maxCols = SIZE_MAX_COLS[size] ?? 16;
    const ids = getIds(block);
    for (const id of ids) {
        const target = block.getInput(`VAL_${id}`)?.connection?.targetBlock();
        if (!target) continue;
        const field = target.getField('TEXT');
        if (!field) continue;
        const current = field.getValue() ?? '';
        const truncated = Array.from(current).slice(0, maxCols).join('');
        if (truncated !== current) field.setValue(truncated);
    }
}

function trimRowsToMax(block: Blockly.Block, size: string): void {
    if (!block.workspace) return;
    const maxRows = SIZE_MAX_ROWS[size] ?? 4;
    const ids = getIds(block);
    while (ids.length > maxRows) {
        const lastId = ids[ids.length - 1];
        const target = block.getInput(`VAL_${lastId}`)?.connection?.targetBlock();
        if (target && !target.isShadow()) target.dispose(false);
        if (block.getInput(`VAL_${lastId}`)) block.removeInput(`VAL_${lastId}`);
        ids.pop();
    }
}

function syncCode(block: Blockly.Block): void {
    requestAnimationFrame(() => {
        const { generateArduinoCode } = require('@/(routers)/boards/editor/block/generators/arduino');
        const { useBoardEditorFileStore } = require('@/stores/useBoardEditorFileStore');
        const { useBoardEditorStore } = require('@/stores/useBoardEditorStore');
        const ws = block.workspace as Blockly.WorkspaceSvg;
        const { currentFileId, updateFile, markAsDirty } = useBoardEditorFileStore.getState();
        if (!currentFileId) return;
        const xml = Blockly.Xml.workspaceToDom(ws);
        const xmlText = Blockly.Xml.domToText(xml);
        const generatedCode = generateArduinoCode(ws);
        updateFile(currentFileId, { blockContent: xmlText, codeContent: generatedCode });
        useBoardEditorStore.getState().setCode(generatedCode);
        markAsDirty(currentFileId);
    });
}

function addRow(block: Blockly.Block): void {
    const size = getCurrentSize(block);
    const maxRows = SIZE_MAX_ROWS[size] ?? 4;
    const ids = getIds(block);
    if (ids.length >= maxRows) return;

    Blockly.Events.disable();
    try {
        const newId = Math.random().toString(36).slice(2, 8);
        ids.push(newId);
        getTypes(block)[newId] = 'number';
        appendRow(block, newId);
    } finally {
        Blockly.Events.enable();
        refreshFooter(block);
        (block.workspace as Blockly.WorkspaceSvg).render();
    }
    syncCode(block);
}

function removeLastRow(block: Blockly.Block): void {
    const ids = getIds(block);
    if (ids.length <= 1) return;
    const lastId = ids[ids.length - 1];

    Blockly.Events.disable();
    try {
        const target = block.getInput(`VAL_${lastId}`)?.connection?.targetBlock();
        if (target && !target.isShadow()) target.dispose(false);
        if (block.getInput(`VAL_${lastId}`)) block.removeInput(`VAL_${lastId}`);
        delete getTypes(block)[lastId];
        ids.pop();
    } finally {
        Blockly.Events.enable();
        refreshFooter(block);
        (block.workspace as Blockly.WorkspaceSvg).render();
    }
    syncCode(block);
}

function applyBoardToPositionField(block: Blockly.Block, board: 'orange' | 'blue'): void {
    const field = block.getField('POSITION') as FieldOperatorDropdown | null;
    if (!field) return;
    field.setOptions([...POSITION_OPTIONS[board].options]);
    if (board === 'orange') field.setValue('UPPER_LEFT');
    (block as Blockly.BlockSvg).render();
}

defineMutableBlock({
    type: 'oled_print',
    colour: COLOR,
    tooltip: 'OLEDに文字を表示します',
    movable: true,
    deletable: true,
    previousStatement: true,
    nextStatement: true,

    init(this: Blockly.Block) {
        (this as any)._ids = [] as string[];
        (this as any)._types = {} as Record<string, 'number' | 'string'>;
        this.setColour(COLOR);
        this.setTooltip('OLEDに文字を表示します');
        this.setMovable(true);
        this.setDeletable(true);
        this.setPreviousStatement(true);
        this.setNextStatement(true);

        const firstId = Math.random().toString(36).slice(2, 8);
        (this as any)._ids.push(firstId);
        (this as any)._types[firstId] = 'number';

        appendHeader(this);
        appendRow(this, firstId);
        refreshFooter(this);

        setTimeout(() => {
            const { useBoardEditorStore } = require('@/stores/useBoardEditorStore');
            const board = useBoardEditorStore.getState().board as 'orange' | 'blue';
            applyBoardToPositionField(this, board);
        }, 0);
    },

    mutationToDom(this: Blockly.Block): Element {
        const container = Blockly.utils.xml.createElement('mutation');
        container.setAttribute('ids', getIds(this).join(','));
        const types = getTypes(this);
        container.setAttribute(
            'types',
            getIds(this)
                .map((id) => types[id] ?? 'number')
                .join(','),
        );
        container.setAttribute('position', this.getFieldValue('POSITION') ?? 'UPPER_LEFT');
        container.setAttribute('size', this.getFieldValue('TEXT_SIZE') ?? '1');
        container.setAttribute('bg', this.getFieldValue('BG_COLOR') ?? 'BLACK');
        return container;
    },

    domToMutation(this: Blockly.Block, xml: Element): void {
        for (const id of getIds(this)) {
            if (this.getInput(`VAL_${id}`)) this.removeInput(`VAL_${id}`);
        }
        if (this.getInput('FOOTER')) this.removeInput('FOOTER');

        const raw = xml.getAttribute('ids') ?? '';
        const ids = raw ? raw.split(',').filter(Boolean) : [];
        const rawTypes = xml.getAttribute('types') ?? '';
        const typeList = rawTypes ? rawTypes.split(',') : [];
        const position = xml.getAttribute('position') ?? 'UPPER_LEFT';
        const size = xml.getAttribute('size') ?? '1';
        const bg = xml.getAttribute('bg') ?? 'BLACK';

        if (ids.length === 0) ids.push(Math.random().toString(36).slice(2, 8));

        (this as any)._ids = ids;
        (this as any)._types = {} as Record<string, 'number' | 'string'>;
        ids.forEach((id, i) => {
            (this as any)._types[id] = (typeList[i] === 'string' ? 'string' : 'number') as 'number' | 'string';
        });

        if (!this.getInput('HEADER')) appendHeader(this);

        requestAnimationFrame(() => {
            (this.getField('TEXT_SIZE') as FieldOperatorDropdown | null)?.setValue(size);
            (this.getField('BG_COLOR') as FieldOperatorDropdown | null)?.setValue(bg);

            const { useBoardEditorStore } = require('@/stores/useBoardEditorStore');
            const board = useBoardEditorStore.getState().board as 'orange' | 'blue';
            const field = this.getField('POSITION') as FieldOperatorDropdown | null;
            if (field) {
                field.setOptions([...POSITION_OPTIONS[board].options]);
                field.setValue(position);
            }
            (this as Blockly.BlockSvg).render();
        });

        ids.forEach((id) => appendRow(this, id));
        refreshFooter(this);
    },

    onBoardChange(block: Blockly.Block, board: 'orange' | 'blue') {
        applyBoardToPositionField(block, board);
    },
});
